import React, { useEffect, useMemo, useState } from "react";
import api, { photoUrl } from "@/lib/api";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ExternalLink,
  FileText,
  Loader2,
  Package,
  Plus,
  Search as SearchIcon,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { useCart } from "@/context/CartContext";
import PromoBannerStrip from "@/components/PromoBannerStrip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 3 — Streamlined, spec-sheet-centric search.
 *
 * Three steps:
 *   1) Manufacturer cards
 *   2) Type cards (with aka chips)
 *   3) Spec-sheet result cards (dedup by name+pdf)
 *
 * Falls back to the legacy AI wizard for any manufacturer flagged
 * search_mode="guided".
 */
export default function StreamlinedSearchFlow({
  activeAgency,
  activeId,
  onUseGuidedWizard,
  pricingEmails,
}) {
  const [manufacturers, setManufacturers] = useState(null);
  const [selectedMfr, setSelectedMfr] = useState(null);
  const [types, setTypes] = useState(null);
  const [typesLoading, setTypesLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [search, setSearch] = useState("");
  const [sheets, setSheets] = useState(null);
  const [sheetsLoading, setSheetsLoading] = useState(false);

  // Step 1: load manufacturers when active agency changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setManufacturers(null);
      setSelectedMfr(null);
      setTypes(null);
      setSelectedType(null);
      setSheets(null);
      try {
        const { data } = await api.get("/streamlined/manufacturers", {
          params: activeId ? { rep_agency_id: activeId } : {},
        });
        if (!cancelled) setManufacturers(data || []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, "Could not load manufacturers"));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  // Step 2: when mfr is selected, fetch its distinct types
  useEffect(() => {
    if (!selectedMfr) return;
    if (selectedMfr.search_mode === "guided") {
      onUseGuidedWizard?.(selectedMfr);
      return;
    }
    let cancelled = false;
    setTypesLoading(true);
    api
      .get("/streamlined/types", {
        params: {
          manufacturer_id: selectedMfr.id,
          ...(activeId ? { rep_agency_id: activeId } : {}),
        },
      })
      .then(({ data }) => {
        if (!cancelled) setTypes(data || []);
      })
      .catch((e) => {
        if (!cancelled) toast.error(formatApiError(e, "Could not load product types"));
      })
      .finally(() => {
        if (!cancelled) setTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMfr, activeId, onUseGuidedWizard]);

  // Step 3: spec-sheet fetch when type chosen or search submitted
  const runSearch = async ({ type, query } = {}) => {
    if (!selectedMfr) return;
    setSheetsLoading(true);
    try {
      const { data } = await api.get("/streamlined/spec-sheets", {
        params: {
          manufacturer_id: selectedMfr.id,
          ...(type ? { type } : {}),
          ...(query ? { query } : {}),
          ...(activeId ? { rep_agency_id: activeId } : {}),
        },
      });
      setSheets(data || []);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load spec sheets"));
    } finally {
      setSheetsLoading(false);
    }
  };

  const onPickType = (t) => {
    setSelectedType(t);
    setSearch("");
    runSearch({ type: t.type });
  };

  const onFreeSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    setSelectedType(null);
    runSearch({ query: search.trim() });
  };

  const resetToManufacturers = () => {
    setSelectedMfr(null);
    setTypes(null);
    setSelectedType(null);
    setSheets(null);
    setSearch("");
  };

  const resetToTypes = () => {
    setSelectedType(null);
    setSheets(null);
    setSearch("");
  };

  if (manufacturers === null) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading manufacturers…
      </div>
    );
  }

  // STEP 3: results
  if (selectedMfr && (sheets !== null || sheetsLoading)) {
    return (
      <ResultsStep
        mfr={selectedMfr}
        selectedType={selectedType}
        search={search}
        setSearch={setSearch}
        onFreeSearch={onFreeSearch}
        sheets={sheets}
        loading={sheetsLoading}
        onBack={resetToTypes}
        onBackToMfrs={resetToManufacturers}
        pricingEmails={pricingEmails}
      />
    );
  }

  // STEP 2: type cards
  if (selectedMfr) {
    return (
      <TypesStep
        mfr={selectedMfr}
        types={types}
        loading={typesLoading}
        search={search}
        setSearch={setSearch}
        onFreeSearch={onFreeSearch}
        onPickType={onPickType}
        onBack={resetToManufacturers}
      />
    );
  }

  // STEP 1: manufacturer cards
  return (
    <ManufacturersStep
      manufacturers={manufacturers}
      activeAgency={activeAgency}
      onPick={(m) => setSelectedMfr(m)}
    />
  );
}

// -------------------- Step 1 --------------------
function ManufacturersStep({ manufacturers, activeAgency, onPick }) {
  if (manufacturers.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <div className="text-sm text-muted-foreground">
          No manufacturers are available for {activeAgency?.name || "this agency"} yet.
        </div>
      </div>
    );
  }
  return (
    <div data-testid="streamlined-step-mfrs">
      <StepHeading
        step={1}
        total={3}
        title="Which manufacturer would you like to search?"
        subtitle="Tap a logo to see what they offer."
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
        {manufacturers.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onPick(m)}
            className="group bg-white border border-border hover:border-amber-400 hover:shadow-sm rounded-sm p-4 flex flex-col items-center text-center transition-all"
            data-testid={`streamlined-mfr-card-${m.id}`}
          >
            <div className="w-20 h-20 bg-slate-50 rounded-sm overflow-hidden flex items-center justify-center mb-3">
              {m.logo_photo_id ? (
                <img
                  src={photoUrl(m.logo_photo_id)}
                  alt={m.name}
                  className="w-full h-full object-contain p-1.5"
                />
              ) : (
                <Building2 className="w-8 h-8 text-slate-300" strokeWidth={1.5} />
              )}
            </div>
            <div className="font-display font-bold text-sm text-slate-900 group-hover:text-amber-700">
              {m.name}
            </div>
            <div className="mt-1 flex flex-wrap items-center justify-center gap-1.5 text-[10px] font-mono-data uppercase tracking-wider text-muted-foreground">
              <span>{m.spec_sheet_count} spec sheets</span>
              {m.search_mode === "guided" && (
                <span className="text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                  AI wizard
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// -------------------- Step 2 --------------------
function TypesStep({
  mfr,
  types,
  loading,
  search,
  setSearch,
  onFreeSearch,
  onPickType,
  onBack,
}) {
  return (
    <div data-testid="streamlined-step-types">
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-muted-foreground hover:text-slate-900 inline-flex items-center gap-1 mb-3"
        data-testid="streamlined-back-to-mfrs"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> All manufacturers
      </button>
      <PromoBannerStrip manufacturerId={mfr.id} />
      <StepHeading
        step={2}
        total={3}
        title={`What do you need a spec sheet for?`}
        subtitle={
          <>
            Browsing <span className="font-semibold text-slate-700">{mfr.name}</span>.
            Pick a type or type what you need below.
          </>
        }
      />
      <SearchBox
        value={search}
        onChange={setSearch}
        onSubmit={onFreeSearch}
        placeholder='Search — e.g. "area light", "parking lot light", "exhaust fan"'
      />
      {loading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading types…
        </div>
      ) : !types || types.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-muted-foreground">
            No spec sheets are loaded for {mfr.name} yet.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
          {types.map((t) => (
            <button
              key={t.type}
              type="button"
              onClick={() => onPickType(t)}
              className="bg-white border border-border hover:border-amber-400 hover:shadow-sm rounded-sm p-4 text-left transition-all group"
              data-testid={`streamlined-type-card-${t.type}`}
            >
              <div className="font-display font-bold text-sm text-slate-900 group-hover:text-amber-700">
                {t.type}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground font-mono-data inline-flex items-center gap-1">
                {t.spec_sheet_count} spec sheet{t.spec_sheet_count === 1 ? "" : "s"}
                <ArrowRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// -------------------- Step 3 --------------------
function ResultsStep({
  mfr,
  selectedType,
  search,
  setSearch,
  onFreeSearch,
  sheets,
  loading,
  onBack,
  onBackToMfrs,
  pricingEmails,
}) {
  return (
    <div data-testid="streamlined-step-results">
      <div className="flex items-center flex-wrap gap-2 text-xs text-muted-foreground mb-3">
        <button
          type="button"
          onClick={onBackToMfrs}
          className="hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="streamlined-back-to-mfrs"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Manufacturers
        </button>
        <span>/</span>
        <button
          type="button"
          onClick={onBack}
          className="hover:text-slate-900"
          data-testid="streamlined-back-to-types"
        >
          {mfr.name}
        </button>
        {selectedType && (
          <>
            <span>/</span>
            <span className="text-slate-700 font-semibold">{selectedType.type}</span>
          </>
        )}
      </div>
      <PromoBannerStrip manufacturerId={mfr.id} />
      <StepHeading
        step={3}
        total={3}
        title={
          selectedType
            ? `${selectedType.type} spec sheets`
            : `Searching ${mfr.name}`
        }
        subtitle="Open the spec sheet to read the ordering matrix, then add it to your cart and tell us which options you need."
      />
      <SearchBox
        value={search}
        onChange={setSearch}
        onSubmit={onFreeSearch}
        placeholder="Let us know which options you need"
      />
      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Searching…
        </div>
      ) : !sheets || sheets.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 border border-border rounded-sm">
          <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-muted-foreground">
            No matching spec sheets. Try a different type or search term.
          </div>
        </div>
      ) : (
        <>
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-mono-data mb-2">
            {sheets.length} spec sheet{sheets.length === 1 ? "" : "s"}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sheets.map((s) => (
              <SpecSheetCard key={s.id} sheet={s} />
            ))}
          </div>
          {pricingEmails && pricingEmails.length > 0 && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-900">
              Have questions? Email{" "}
              {pricingEmails.map((e, i) => (
                <React.Fragment key={e}>
                  <a
                    href={`mailto:${e}`}
                    className="font-semibold underline hover:text-amber-700"
                  >
                    {e}
                  </a>
                  {i < pricingEmails.length - 2
                    ? ", "
                    : i === pricingEmails.length - 2
                    ? " or "
                    : ""}
                </React.Fragment>
              ))}
              .
            </div>
          )}
        </>
      )}
    </div>
  );
}

// -------------------- Spec sheet card --------------------
function SpecSheetCard({ sheet }) {
  const { addItem, setDrawerOpen, cart } = useCart();
  const [adding, setAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const inCart = (cart.items || []).some((it) => it.product_id === sheet.id);

  const fullSpecUrl = sheet.spec_sheet_url
    ? sheet.spec_sheet_url.startsWith("http")
      ? sheet.spec_sheet_url
      : `${BACKEND_URL}${sheet.spec_sheet_url}`
    : null;

  const handleAdd = async () => {
    setAdding(true);
    try {
      await addItem(sheet.id, { quantity: 1 });
      setJustAdded(true);
      toast.success(`Added "${sheet.type}" to your quote cart`, {
        action: { label: "View cart", onClick: () => setDrawerOpen(true) },
      });
      setTimeout(() => setJustAdded(false), 1800);
    } catch (err) {
      toast.error(formatApiError(err, "Could not add to cart"));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      className="bg-white border border-border hover:border-amber-300 rounded-sm p-4 flex flex-col gap-3 transition-all"
      data-testid="streamlined-spec-card"
    >
      <div className="flex items-start gap-3">
        {sheet.primary_photo_id ? (
          <img
            src={photoUrl(sheet.primary_photo_id)}
            alt={sheet.type || ""}
            className="w-16 h-16 object-cover rounded-sm border border-border shrink-0 bg-white"
            data-testid="streamlined-card-photo"
          />
        ) : (
          <div className="w-12 h-12 bg-amber-50 border border-amber-200 rounded-sm flex items-center justify-center shrink-0">
            <FileText className="w-6 h-6 text-amber-700" strokeWidth={1.5} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-slate-900 leading-tight">
            {sheet.type || "—"}
          </div>
          {sheet.name && (
            <div className="text-xs text-slate-600 mt-0.5">{sheet.name}</div>
          )}
          {sheet.description && (
            <div className="mt-2 text-[11px] bg-slate-50 border-l-2 border-amber-300 pl-2 py-1 text-slate-700 italic">
              <span className="not-italic uppercase tracking-wider text-[9px] font-mono-data text-amber-700 mr-1">
                Additional Notes:
              </span>
              {sheet.description}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-auto">
        {fullSpecUrl ? (
          <a
            href={fullSpecUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-2 rounded-sm transition-colors"
            data-testid="streamlined-view-spec-btn"
          >
            <FileText className="w-3.5 h-3.5" />
            View Spec Sheet
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : (
          <span className="inline-flex items-center justify-center gap-1.5 bg-slate-100 text-muted-foreground text-xs py-2 rounded-sm italic">
            No spec sheet
          </span>
        )}
        <button
          type="button"
          onClick={handleAdd}
          disabled={adding}
          className={`inline-flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-sm transition-colors border ${
            justAdded || inCart
              ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              : "bg-amber-500 hover:bg-amber-600 border-amber-500 text-slate-900"
          }`}
          data-testid="streamlined-add-to-cart-btn"
        >
          {adding ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : justAdded ? (
            <>
              <Check className="w-3.5 h-3.5" />
              Added
            </>
          ) : inCart ? (
            <>
              <Check className="w-3.5 h-3.5" />
              In cart · Add another
            </>
          ) : (
            <>
              <Plus className="w-3.5 h-3.5" />
              Add to cart
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// -------------------- Shared bits --------------------
function StepHeading({ step, total, title, subtitle }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-amber-600 font-mono-data">
        Step {step} of {total}
      </div>
      <h2 className="text-xl sm:text-2xl font-display font-bold tracking-tight text-slate-900 mt-1">
        {title}
      </h2>
      {subtitle && (
        <div className="text-sm text-muted-foreground mt-1">{subtitle}</div>
      )}
    </div>
  );
}

function SearchBox({ value, onChange, onSubmit, placeholder }) {
  return (
    <form onSubmit={onSubmit} className="relative my-4">
      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-sm pl-9 h-11"
        data-testid="streamlined-search-input"
      />
    </form>
  );
}
